import React from 'react';
import queryString from 'query-string';
import { SubjectSiteAccount, SubjectSiteShell } from 'api/subjectSiteApi';
import { AppPath, PUBLIC_SITE_URL } from 'appConstants';
import { withScheme } from 'helpers/subjectSites';

export type SubjectSiteLink = {
  name: string;
  url: string;
  /**
   * The link is a route of this app
   */
  bInApp?: boolean;
  bBold?: boolean;
};

const TOKEN_PLACEHOLDER = 'AUTOLOGINTOKEN';

/**
 * Signs the user out, as the subject site's Logout does on its way back to sign in
 */
export const SIGN_OUT_LINK: SubjectSiteLink = {
  name: 'Logout',
  url: `${AppPath.SIGN_IN}?auth=sign-out`,
  bInApp: true,
};

/**
 * Pages of the subject site that have a route in this app
 */
const APP_PATH_PREFIXES = ['/lesson/', '/resourcebank'];

/**
 * Runs an element's action from the keyboard, as a button does
 */
export const onEnterOrSpace =
  (action: () => void) => (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      action();
    }
  };

/**
 * A subject content link is a path on the subject site: the app's own route
 * when the app has the page, else the page on the public site. Full URLs are
 * kept as they are.
 */
export const subjectContentLink = (
  subjectCode: string,
  name: string,
  url: string
): SubjectSiteLink => {
  if (/^https?:\/\//i.test(url)) return { name, url };

  const queryStart = url.search(/[?#]/);
  const path = `/${(queryStart === -1 ? url : url.slice(0, queryStart))
    .replace(/^\/+/, '')
    .toLowerCase()}`;
  const rest = queryStart === -1 ? '' : url.slice(queryStart);
  const bInApp = APP_PATH_PREFIXES.some((prefix) => path.startsWith(prefix));

  return {
    name,
    url: `${bInApp ? '' : PUBLIC_SITE_URL}/${subjectCode}${path}${rest}`,
    bInApp,
  };
};

/**
 * A school page that only the subject site has, reached by signing in to it
 * with an auto-login token, as the dashboard's subject links do
 * @param area - The subject site path to land on, for example "school/pupil/index"
 */
export const subjectSitePageUrl = (
  account: SubjectSiteAccount | null,
  area: string
) =>
  account?.subjectSiteLink
    ? `${AppPath.DASHBOARD}?${queryString.stringify({
        redirectURL: `${withScheme(
          account.subjectSiteLink
        )}?token=${TOKEN_PLACEHOLDER}`,
        area,
      })}`
    : AppPath.DASHBOARD;

/**
 * The header's links after Lesson Plans, in the subject site's order
 */
export const subjectSiteMenuLinks = (
  subjectCode: string,
  shell: SubjectSiteShell | null,
  account: SubjectSiteAccount | null
): SubjectSiteLink[] => {
  const activeFamiliesName = shell?.contents.ActiveFamilies;
  const activeFamiliesUrl = shell?.contents.ActiveFamiliesURL;

  return [
    {
      name: 'Awesome Stuff',
      url: `/${subjectCode}/resourcebank/awesomestuff`,
      bInApp: true,
    },
    ...(shell?.cupboardTitle
      ? [
          {
            name: shell.cupboardTitle,
            url: `${PUBLIC_SITE_URL}/${subjectCode}/virtual-cupboard`,
          },
        ]
      : []),
    ...(account?.bPaidSubscription
      ? [{ name: 'Help', url: subjectSitePageUrl(account, 'help') }]
      : [
          ...(activeFamiliesName && activeFamiliesUrl
            ? [
                subjectContentLink(
                  subjectCode,
                  activeFamiliesName,
                  activeFamiliesUrl
                ),
              ]
            : []),
          { name: 'Book a Meet', url: `${PUBLIC_SITE_URL}/workshops` },
        ]),
  ];
};

/**
 * The school pages under the school's name, for a school with a paid
 * subscription to the subject
 */
export const subjectSiteSchoolLinks = (
  shell: SubjectSiteShell | null,
  account: SubjectSiteAccount | null
): SubjectSiteLink[] =>
  account?.bPaidSubscription
    ? [
        {
          name: 'My Classes',
          url: subjectSitePageUrl(account, 'school/myclass/index'),
        },
        {
          name: 'My Pupils',
          url: subjectSitePageUrl(account, 'school/pupil/index'),
        },
        {
          name: `My Clubs and ${shell?.contents.Competitions ?? ''}`,
          url: subjectSitePageUrl(account, 'school/clubsandcompetitions/index'),
        },
        {
          name: 'My Curriculum Map',
          url: subjectSitePageUrl(account, 'school/curriculummap/index'),
        },
        {
          name: 'My Reporting',
          url: subjectSitePageUrl(account, 'school/reporting/compare'),
        },
        {
          name: 'My Documents',
          url: shell
            ? `${AppPath.MY_DOCUMENTS}?subjectsid=${shell.subject.subjectsID}`
            : AppPath.MY_DOCUMENTS,
          bInApp: true,
        },
      ]
    : [];

/**
 * The school's account links under its name in the header
 */
export const subjectSiteAccountLinks = (
  account: SubjectSiteAccount | null
): SubjectSiteLink[] => [
  ...(account?.bFreeTrial
    ? [
        {
          name: 'Upgrade to Subscription',
          url: AppPath.UPGRADE_SUBSCRIPTION,
          bInApp: true,
          bBold: true,
        },
      ]
    : []),
  ...(account?.bMainAccountHolder
    ? [
        { name: 'Manage School', url: AppPath.MANAGESCHOOL, bInApp: true },
        { name: 'Manage Users', url: AppPath.MANAGEUSERS, bInApp: true },
      ]
    : []),
];
